import React from "react";
import Contact from "../contact";
import arrowIcon from "../../images/home/arrow-icon.svg";
import benifitIcon from "../../images/individual/benifitIcon.png";
import { FaArrowUp, FaCalendarAlt, FaExternalLinkAlt, FaUsers, FaBriefcase, FaMapMarkerAlt, FaChartLine, FaGlobe, FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const MigrationProgram202425 = () => {
  return (
    <div>
      <Helmet>
        <title>Migration Program 2024–25: Focus on Skills and Regional Australia - Immiget Global</title>
        <meta
          name="description"
          content="Comprehensive guide to Australia's Migration Program 2024-25 with 185,000 places. 71% allocated to Skill stream addressing labor shortages in critical sectors."
        />
        <meta 
          name="keywords" 
          content="migration program 2024-25, australia migration planning, skill stream visa, regional australia visa, permanent migration australia" 
        />
      </Helmet>
      
      <div className="w-full h-90 font-Hellix bg-cover bg-center bg-image">
        <div className="max-w-[1440px] px-4 md:px-[75px] m-auto h-full">
          <div className="flex flex-col gap-2 md:gap-2 pt-10 md:pt-20 pb-[90px] h-full justify-center">
            <div className="text-white hellix-font text-center font-semibold text-[22px] md:text-[54px]">
              Migration Program 2024–25
            </div>
            <div className="text-white hellix-font text-center font-medium text-[16px] md:text-[24px] mb-4">
              Focus on Skills and Regional Australia
            </div>
            <div className="flex justify-center gap-2 md:gap-4">
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/" className="hover:underline">HOME</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/news" className="hover:underline">NEWS</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium uppercase">
                Migration Program 2024-25
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <div className="container max-w-1440px mx-auto px-4 py-6 xl:py-10 lg:px-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue text-white p-3 rounded-full">
              <FaArrowUp className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-blue text-sm hellix-font font-medium">
                <FaCalendarAlt className="text-xs" />
                <span>2024-25</span>
              </div>
              <div className="text-blue text-sm hellix-font">Migration Policy Update</div>
            </div>
          </div>
          
          <h1 className="xl:text-[42px] text-[32px] text-blue font-bold hellix-font mb-6 leading-tight">
            Migration Program 2024–25: Focus on Skills and Regional Australia
          </h1>
          
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <FaInfoCircle className="text-blue text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Key Highlight</h3>
                <p className="text-blue hellix-font text-lg leading-relaxed">
                  The Australian Government has set the permanent Migration Program planning levels at <strong>185,000 places for 2024–25</strong>, with approximately <strong>71% allocated to the Skill stream</strong> to address labor shortages in critical sectors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-1440px mx-auto px-4 lg:px-14">
        <div className="max-w-4xl mx-auto">
          
          {/* Overview Section */}
          <section className="mb-12">
            <div className="flex gap-2 mb-6">
              <div className="flex items-center">
                <img src={benifitIcon} alt="benefits" />
              </div>
              <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font">
                Program Overview
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-blue hellix-font text-lg leading-relaxed mb-6">
                Australia's Migration Program for 2024–25 represents a strategic approach to addressing the nation's evolving economic needs while maintaining a focus on regional development and skills-based immigration. This comprehensive program sets clear priorities for permanent migration pathways.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-blue">
                  <div className="flex items-center gap-3 mb-4">
                    <FaUsers className="text-blue text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-xl">Total Places</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue hellix-font">185,000</p>
                  <p className="text-blue hellix-font">Permanent migration places for 2024-25</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-green-500">
                  <div className="flex items-center gap-3 mb-4">
                    <FaBriefcase className="text-green-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-xl">Skill Stream</h3>
                  </div>
                  <p className="text-3xl font-bold text-green-600 hellix-font">71%</p>
                  <p className="text-blue hellix-font">Of total places allocated to skilled migration</p>
                </div>
              </div>
            </div>
          </section>

          {/* Key Features Section */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaChartLine className="text-blue" />
              Key Features of the 2024-25 Program
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaCheckCircle className="text-green-500 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Skills-Based Priority</h3>
                    <p className="text-blue hellix-font leading-relaxed">
                      The significant allocation to the Skill stream reflects Australia's commitment to attracting talented professionals who can contribute to economic growth and fill critical labor shortages across various industries.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaMapMarkerAlt className="text-blue text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Regional Australia Focus</h3>
                    <p className="text-blue hellix-font leading-relaxed">
                      Priority visa processing for regional areas aims to support population growth and economic development outside major cities, addressing regional skill shortages and supporting local communities.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaBriefcase className="text-blue text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Critical Sector Targeting</h3>
                    <p className="text-blue hellix-font leading-relaxed">
                      The program specifically targets labor shortages in critical sectors including healthcare, technology, engineering, trades, and other essential services that support Australia's economic infrastructure.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>



          {/* Official Resources */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Official Resources
            </h2>
            
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-blue hellix-font mb-4">
                For the most up-to-date information about the Migration Program 2024-25, visit the official government resources:
              </p>
              <a
                href="https://immi.homeaffairs.gov.au/what-we-do/migration-program-planning-levels"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue font-medium hellix-font hover:text-blue-700 transition-colors"
              >
                <FaExternalLinkAlt className="text-sm" />
                Department of Home Affairs - Migration Program Planning Levels
              </a>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Related Immigration News
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/news/employer-sponsored-visas-2024-25" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaBriefcase className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">Employer Visas</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Employer-Sponsored Visas: Increased Planning Levels</h3>
                <p className="text-blue hellix-font text-sm">44,000 visas allocated for 2024-25, supporting pathways to permanent residency...</p>
              </Link>
              
              <Link to="/news" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaGlobe className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">All News</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">View All Immigration News</h3>
                <p className="text-blue hellix-font text-sm">Stay updated with the latest immigration policies and opportunities...</p>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Contact Section */}
      <Contact source="Migration Program 2024-25 Blog" />
    </div>
  );
};

export default MigrationProgram202425;